import { guest, userInjectionKey, type MutationCallbacks } from '#shared'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { inject, unref, type MaybeRef } from 'vue'
import { getMessagesQueryKey, type MessagesQueryData } from '#entities/message'
import { addReaction } from '../../../widgets/message-list/api/add-reaction'
import type { AddReactionPayload } from '../../../widgets/message-list/models/add-reaction-payload'
import type { AddReactionResponse } from '../../../widgets/message-list/models/add-reaction-response'

type AddReactionMutationCallbacks = MutationCallbacks<AddReactionPayload, AddReactionResponse>

export function useAddReactionMutation(
  chatId: MaybeRef<number>,
  messageId: MaybeRef<number>,
  callbacks: AddReactionMutationCallbacks = {}
) {
  const currentUser = inject(userInjectionKey)
  const queryClient = useQueryClient()

  return useMutation({
    ...callbacks,

    mutationFn: (payload: AddReactionPayload) => addReaction(unref(chatId), unref(messageId), payload),

    onSuccess(response, payload) {
      const currentUserVal = unref(currentUser)
      if (!currentUserVal || currentUserVal === guest) return

      response.data.sender = currentUserVal

      queryClient.setQueryData(getMessagesQueryKey(unref(chatId)), (queryData: MessagesQueryData | undefined) => {
        if (!queryData) return

        const newPages = queryData?.pages.map(page => {
          if (!page.data.some(message => message.id === unref(messageId))) return page

          const newData = page.data.map(message => {
            if (message.id !== unref(messageId)) return message
            if (
              message.reactions.some(
                reaction => reaction.sender?.id === currentUserVal.id && reaction.char === payload.char
              )
            )
              return message

            return {
              ...message,
              reactions: [...message.reactions, response.data],
            }
          })

          return {
            ...page,
            data: newData,
          }
        })

        return {
          ...queryData,
          pages: newPages,
        }
      })

      callbacks.onSuccess?.(response, payload)
    },
  })
}
